<script setup lang="ts">
/**
 * Focused graph views, each answering one question — never the whole graph:
 * neighbourhood (depth 1–2), lineage topology, impact ("what breaks if X is
 * down?") and path finder ("how is A connected to B?").
 */
import { ArrowRight, Crosshair, ExternalLink, Network, Route, ShieldAlert, Waypoints } from "@lucide/vue";
import { useMediaQuery } from "@vueuse/core";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { isGroupNode, type EntityRef, type GraphNode } from "@/api/contracts";
import { useGraph, useKnowledgeSearch, type GraphView } from "@/api/knowledge";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import GraphCanvas from "@/components/graph/GraphCanvas.vue";
import EntityPicker from "@/components/knowledge/EntityPicker.vue";
import KnowledgeNav from "@/components/knowledge/KnowledgeNav.vue";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { t } from "@/copy/strings";
import { entityPath, typeInfo } from "@/lib/knowledge";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Knowledge views", layout: "full", requires: { capability: "knowledge.read" } } });

/** Tall enough for a readable graph on laptops, never taller than the screen. */
const CANVAS_HEIGHT = "min(70dvh, 48rem)";
// Phones are tall and narrow: the layers go top to bottom there.
const phone = useMediaQuery("(max-width: 639.98px)");

const router = useRouter();
const view = useRouteQuery("view", "neighborhood");
const uri = useRouteQuery("uri");
const depth = useRouteQuery("depth", "1");
const lineage = useRouteQuery("lineage");
const from = useRouteQuery("from");
const to = useRouteQuery("to");

const VIEWS = [
  { id: "neighborhood", label: "Neighbourhood", question: "What is directly connected to this?", icon: Network },
  { id: "topology", label: "Lineage topology", question: "What makes up a lineage?", icon: Waypoints },
  { id: "impact", label: "Impact", question: "What breaks if this is down?", icon: ShieldAlert },
  { id: "path", label: "Path finder", question: "How are these two connected?", icon: Route },
] as const;
const current = computed(() => VIEWS.find((v) => v.id === view.value) ?? VIEWS[0]);

function refFromUri(value: string): EntityRef | null {
  const m = /^knowledge:\/\/entity\/([^/]+)\/(.+)$/.exec(value);
  if (!m) return null;
  // Prefer the real name once the graph has loaded it.
  const known = graphNodes.value.find((n) => n.uri === value);
  return { type: m[1], slug: m[2], name: known?.name ?? m[2].replace(/-/g, " "), uri: value };
}
function pickerModel(r: typeof uri) {
  return computed<EntityRef | null>({ get: () => (r.value ? refFromUri(r.value) : null), set: (v) => (r.value = v?.uri ?? "") });
}
const graphNodes = computed<GraphNode[]>(() => graph.data.value?.nodes ?? []);
const subject = pickerModel(uri);
const fromRef = pickerModel(from);
const toRef = pickerModel(to);

const lineages = useKnowledgeSearch(() => ({ q: "", types: ["lineage"], limit: 50 }));
const lineageOptions = computed(() => (lineages.data.value?.hits ?? []).map((h) => h.slug ?? h.title));

const params = computed<Record<string, string | number | undefined>>(() => {
  if (view.value === "topology") return { lineage: lineage.value || undefined };
  if (view.value === "path") return { from: from.value || undefined, to: to.value || undefined };
  if (view.value === "impact") return { uri: uri.value || undefined };
  return { uri: uri.value || undefined, depth: Number(depth.value) || 1 };
});
const ready = computed(() => {
  if (view.value === "topology") return Boolean(lineage.value);
  if (view.value === "path") return Boolean(from.value && to.value);
  return Boolean(uri.value);
});
const graph = useGraph(() => current.value.id as GraphView, params, ready);
const focusId = computed(() => graph.data.value?.nodes.find((n) => n.uri === (view.value === "path" ? from.value : uri.value))?.id);
const impacted = computed(() => (view.value === "impact" ? (graph.data.value?.nodes ?? []).filter((n) => n.id !== focusId.value && !isGroupNode(n)) : []));
const listNodes = computed(() => (graph.data.value?.nodes ?? []).filter((n) => !isGroupNode(n)));

const selected = ref<GraphNode | null>(null);

function setView(id: string) {
  selected.value = null;
  void router.replace({ query: { view: id, ...(id === "neighborhood" || id === "impact" ? { uri: uri.value || undefined } : {}) } });
}

function centerOn(n: GraphNode) {
  selected.value = null;
  if (view.value === "neighborhood" || view.value === "impact") uri.value = n.uri;
  else void router.replace({ query: { view: "neighborhood", uri: n.uri } });
}
</script>

<template>
  <div>
    <PageHeader :title="t.knowledge.views" description="Small, focused maps that each answer one question." />
    <KnowledgeNav />

    <div role="tablist" aria-label="View" class="mb-5 grid grid-cols-2 gap-2 lg:grid-cols-4">
      <button
        v-for="v in VIEWS"
        :key="v.id"
        role="tab"
        type="button"
        :aria-selected="current.id === v.id"
        :class="
          cn(
            'flex items-start gap-3 rounded-xl border bg-card p-3 text-left shadow-xs transition-[border-color,background-color,box-shadow] duration-150 hover:border-border-strong hover:shadow-sm active:bg-state-press',
            current.id === v.id && 'border-primary bg-state-selected ring-1 ring-primary/30 hover:border-primary',
          )
        "
        @click="setView(v.id)"
      >
        <component :is="v.icon" class="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
        <span class="min-w-0">
          <span class="block text-sm font-medium">{{ v.label }}</span>
          <span class="hidden text-xs text-muted-foreground sm:block">{{ v.question }}</span>
        </span>
      </button>
    </div>

    <!-- Inputs for the selected view -->
    <Card padding="none" class="mb-5 gap-3 p-4 sm:flex-row sm:items-end">
      <template v-if="current.id === 'neighborhood' || current.id === 'impact'">
        <div class="flex flex-1 flex-col gap-1.5">
          <Label for="v-subject">{{ current.id === "impact" ? "If this is down…" : "Around" }}</Label>
          <EntityPicker id="v-subject" v-model="subject" placeholder="e.g. orders-db, Checkout Service" label="entity" />
        </div>
        <div v-if="current.id === 'neighborhood'" class="flex flex-col gap-1.5">
          <Label>Depth</Label>
          <ToggleGroup :model-value="depth" type="single" variant="outline" aria-label="Depth" @update:model-value="(v) => v && (depth = String(v))">
            <ToggleGroupItem value="1">1 hop</ToggleGroupItem>
            <ToggleGroupItem value="2">2 hops</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </template>
      <div v-else-if="current.id === 'topology'" class="flex flex-1 flex-col gap-1.5 sm:max-w-xs">
        <Label>Lineage</Label>
        <SimpleSelect v-model="lineage" :options="lineageOptions" placeholder="Choose a lineage" aria-label="Lineage" />
      </div>
      <template v-else>
        <div class="flex flex-1 flex-col gap-1.5">
          <Label for="v-from">From</Label>
          <EntityPicker id="v-from" v-model="fromRef" label="start" />
        </div>
        <ArrowRight class="mb-2.5 hidden size-4 text-muted-foreground sm:block" aria-hidden="true" />
        <div class="flex flex-1 flex-col gap-1.5">
          <Label for="v-to">To</Label>
          <EntityPicker id="v-to" v-model="toRef" label="end" />
        </div>
      </template>
    </Card>

    <EmptyState v-if="!ready" :icon="current.icon" :title="current.question" description="Pick what you want to look at above." />
    <Skeleton v-else-if="graph.isPending.value" class="w-full rounded-xl" :style="{ height: CANVAS_HEIGHT }" />
    <ErrorState v-else-if="graph.error.value" :error="graph.error.value" @retry="graph.refetch()" />
    <EmptyState v-else-if="!graph.data.value?.nodes.length" :icon="Network" title="Nothing connected" description="No relations recorded yet. Add them on the knowledge pages." />
    <div v-else class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_16rem] 2xl:grid-cols-[minmax(0,1fr)_20rem]">
      <div class="flex min-w-0 flex-col gap-2">
        <p v-if="graph.data.value.truncated" class="rounded-md bg-warning-soft px-3 py-2 text-sm">Showing the 40 closest items only. Narrow it down to see more detail.</p>
        <GraphCanvas
          :nodes="graph.data.value.nodes"
          :edges="graph.data.value.edges"
          :focus-id="focusId"
          :highlight="impacted.map((n) => n.id)"
          :height="CANVAS_HEIGHT"
          :direction="phone ? 'DOWN' : 'RIGHT'"
          :aria-label="current.label"
          @select="(n) => (selected = n)"
        />
      </div>
      <aside class="flex flex-col gap-3">
        <Card v-if="selected" padding="sm" class="sm:p-3">
          <p class="flex items-center gap-2 text-sm font-medium">
            <component :is="typeInfo(selected.type).icon" class="size-4" :style="{ color: typeInfo(selected.type).color }" aria-hidden="true" /> {{ selected.name }}
          </p>
          <div class="flex flex-wrap gap-2">
            <Button as-child size="sm"><RouterLink :to="entityPath(selected.type, selected.slug)"><ExternalLink /> Open page</RouterLink></Button>
            <Button size="sm" variant="outline" @click="centerOn(selected)"><Crosshair /> Center here</Button>
          </div>
        </Card>
        <Card v-if="current.id === 'impact'" padding="sm" class="gap-0 sm:p-3">
          <p class="mb-2 text-sm font-medium">{{ impacted.length }} affected</p>
          <ul class="flex flex-col gap-0.5">
            <li v-for="n in impacted" :key="n.id">
              <RouterLink :to="entityPath(n.type, n.slug)" class="-mx-1.5 flex items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-state-hover">
                <component :is="typeInfo(n.type).icon" class="size-3.5" :style="{ color: typeInfo(n.type).color }" aria-hidden="true" /> {{ n.name }}
              </RouterLink>
            </li>
          </ul>
        </Card>
        <Card v-else padding="sm" class="gap-0 sm:p-3">
          <p class="mb-2 text-sm font-medium">{{ listNodes.length }} items</p>
          <ul class="flex max-h-96 flex-col gap-0.5 overflow-y-auto">
            <li v-for="n in listNodes" :key="n.id">
              <button type="button" class="-mx-1.5 flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-sm hover:bg-state-hover" @click="selected = n">
                <component :is="typeInfo(n.type).icon" class="size-3.5 shrink-0" :style="{ color: typeInfo(n.type).color }" aria-hidden="true" />
                <span class="truncate">{{ n.name }}</span>
              </button>
            </li>
          </ul>
        </Card>
      </aside>
    </div>
  </div>
</template>
